import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import portalApi from './portalApi';
import { T } from './portalTheme';
import { AuthLayout, AuthHeading, Field, TextInput, PasswordInput, Button, Alert, IC } from './PortalWidgets';
import { usePortalAuth } from '../../context/PortalAuthContext';

interface InviteContext { sender_org_name: string | null; questionnaire_name: string | null; recipient_email: string }

// /questionnaire/portal/login?invite=<token> -- also reached directly (no invite) by anyone who
// already has an account and just wants their dashboard, and via ?redirect= from a protected page.
export default function PortalLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const invite = params.get('invite') || '';
  const redirect = params.get('redirect') || '';
  const { login, isAuthenticated } = usePortalAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [context, setContext] = useState<InviteContext | null>(null);

  useEffect(() => {
    if (isAuthenticated) redirectAfterAuth(invite, navigate, redirect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!invite) return;
    portalApi.get('/v1/questionnaire-responses/portal/invite-status', { params: { invite } })
      .then(({ data }) => { setContext(data); setEmail(prev => prev || data.recipient_email); })
      .catch(() => { /* sign-in still works without the context banner */ });
  }, [invite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      redirectAfterAuth(invite, navigate, redirect);
    } catch (err: any) {
      setError(err.message || 'Could not sign in.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <AuthHeading title="Sign in" subtitle="Welcome back. Sign in to see the security questionnaires waiting for you." />
      {context && (
        <div style={{ marginBottom: 20 }}>
          <Alert tone="info" icon={IC.inbox}>
            <strong>{context.sender_org_name || 'A company'}</strong> sent you <strong>{context.questionnaire_name || 'a questionnaire'}</strong>. Sign in to get started.
          </Alert>
        </div>
      )}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Work email">
          <TextInput value={email} onChange={e => setEmail(e.target.value)} type="email" autoComplete="email" required autoFocus={!email} placeholder="you@company.com" />
        </Field>
        <Field label="Password" right={
          <Link to={`/questionnaire/portal/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`} className="pw-link"
            style={{ fontSize: 12.5, color: T.primary, fontWeight: 600, textDecoration: 'none' }}>Forgot password?</Link>
        }>
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required autoFocus={!!email} />
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" size="lg" full loading={submitting}>{submitting ? 'Signing in…' : 'Sign in'}</Button>
      </form>
      <div style={{ fontSize: 13.5, color: T.muted, marginTop: 24, textAlign: 'center' }}>
        {invite ? (
          <>New to FluxWatch?{' '}
            <Link to={`/questionnaire/portal/register?invite=${encodeURIComponent(invite)}`} className="pw-link" style={{ color: T.primary, fontWeight: 600, textDecoration: 'none' }}>
              Create an account
            </Link>
          </>
        ) : (
          <>Don't have an account? Use the link in the email you received from the company that sent you a questionnaire.</>
        )}
      </div>
    </AuthLayout>
  );
}

// Shared by Login, Verify and Reset: land on the overview of the specific questionnaire that
// prompted this sign-in (resolved fresh via invite-status, since we only ever carry the token, not
// the response id) if there is one, back to the page that bounced them here if there is one,
// otherwise the dashboard. Lands on the overview/detail screen, not straight into the answer form.
export async function redirectAfterAuth(invite: string, navigate: ReturnType<typeof useNavigate>, redirect?: string) {
  if (invite) {
    try {
      const { data } = await portalApi.get('/v1/questionnaire-responses/portal/invite-status', { params: { invite } });
      navigate(data.destination || `/questionnaire/portal/response/${data.response_id}`, { replace: true });
      return;
    } catch {
      // fall through if the invite can't be resolved anymore
    }
  }
  if (redirect && redirect.startsWith('/questionnaire/portal/')) {
    navigate(redirect, { replace: true });
    return;
  }
  navigate('/questionnaire/portal/dashboard', { replace: true });
}
